import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { addressAPI } from "../services/api";

export default function AddressPage() {
  const navigate = useNavigate();
  const auth0 = useAuth0();
  const { isAuthenticated, isLoading } = auth0;

  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    street: "",
    city: "",
    pincode: "",
  });

  // 🔐 Load addresses
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return navigate("/login");

    addressAPI(auth0)
      .getAddresses()
      .then((data) => {
        setAddresses(data || []);
        if (data?.length) setSelectedId(data[0].id);
      })
      .catch(() => setError("Please login again"))
      .finally(() => setLoading(false));
  }, [isAuthenticated, isLoading]);

  // ➕ Save address
  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const saved = await addressAPI(auth0).createAddress(form);
      setAddresses([saved, ...addresses]);
      setSelectedId(saved.id);
      setShowForm(false);
    } catch {
      setError("Could not save address");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center mt-20 text-gray-600">
        Loading addresses…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 pb-28">
      {/* TITLE */}
      <h1 className="text-xl font-semibold mb-4">
        Select Delivery Address
      </h1>

      {/* ERROR */}
      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* SAVED ADDRESSES */}
      <div className="space-y-4">
        {addresses.map((addr) => (
          <label
            key={addr.id}
            className={`flex gap-4 p-4 border rounded cursor-pointer transition
              ${
                selectedId === addr.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white"
              }`}
          >
            <input
              type="radio"
              checked={selectedId === addr.id}
              onChange={() => setSelectedId(addr.id)}
              className="mt-1"
            />

            <div>
              <p className="font-medium">{addr.name}</p>
              <p className="text-sm text-gray-600 mt-1">
                {addr.street}, {addr.city} – {addr.pincode}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Phone: {addr.phone}
              </p>
            </div>
          </label>
        ))}
      </div>

      {/* ADD NEW ADDRESS */}
      <div className="mt-6">
        <button
          onClick={() => setShowForm(!showForm)}
          className="text-blue-600 font-medium"
        >
          + Add a new address
        </button>

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="mt-4 bg-white border rounded p-4 space-y-3"
          >
            {["name", "phone", "street", "city", "pincode"].map((field) => (
              <input
                key={field}
                required
                placeholder={field.toUpperCase()}
                value={form[field]}
                onChange={(e) =>
                  setForm({ ...form, [field]: e.target.value })
                }
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ))}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-6 py-2 rounded font-medium disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>

              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-6 py-2 border rounded"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* FIXED CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t">
        <div className="max-w-3xl mx-auto p-4">
          <button
            disabled={!selectedId}
            onClick={() => navigate("/payment")}
            className="w-full bg-orange-500 text-white py-3 rounded font-semibold disabled:opacity-50"
          >
            Deliver Here
          </button>
        </div>
      </div>
    </div>
  );
}
